import { Link, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { DEFAULT_HOME } from '@/auth/routes'
import { Button } from '@/ui/Button'
import { InlineError } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { TONE_LABELS, metaFor, notConfigured, pathFor } from './steps'

/**
 * `/onboarding/resume` — S-D2's `resume`.
 *
 * What a returning, half-set-up tenant sees. **Progress is never lost and never
 * nags**: the dashboard is fully usable with three steps outstanding, and the
 * banner states exactly what is degraded as a result so the tenant can judge
 * whether to care. That is why every outstanding row carries its consequence
 * rather than a generic "not finished".
 *
 * *Don&rsquo;t remind me again* dismisses the shell's banner and nothing else.
 * It does not claim the outstanding steps are done, and this screen still lists
 * them — dismissal is about the reminder, not about the setup.
 */
export function Resume(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const dismiss = useMutation(() => call(() => api.POST('/api/v1/onboarding/dismiss', {})))

  const settled = state.steps.filter((row) => row.status === 'done')
  const outstanding = notConfigured(state)

  async function onDismiss(): Promise<void> {
    const result = await dismiss.run(undefined)
    if (result.ok) {
      adopt(result.data)
      void navigate(DEFAULT_HOME)
    }
  }

  return (
    <div className="app-wiz">
      <div className="ar-alert ar-alert--info">
        <span className="ar-alert__icon" aria-hidden="true">
          ◷
        </span>
        <div className="ar-alert__body">
          <strong>
            Setup is {settled.length} of {state.steps.length} done — pick up where you left off
          </strong>
          {describeDegradation(outstanding.map((row) => row.step))}
        </div>
      </div>

      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Finish setting up</h3>
          <span className="ar-card__spacer" />
          <span className="ar-badge ar-badge--neutral">{outstanding.length} remaining</span>
        </div>

        <div className="ar-card__body ar-stack ar-stack--tight">
          <InlineError failure={dismiss.state.failure} />

          {settled.map((row) => {
            const meta = metaFor(row.step)
            if (!meta) return null
            return (
              <div className="app-conn" key={row.step}>
                <span className="app-conn__logo app-conn__logo--done" aria-hidden="true">
                  ✓
                </span>
                <span className="app-conn__body">
                  <strong>{meta.settled}</strong>
                  <span>{summaryFor(row.step, state)}</span>
                </span>
              </div>
            )
          })}

          {outstanding.map((row) => {
            const meta = metaFor(row.step)
            if (!meta) return null
            const position = state.steps.findIndex((entry) => entry.step === row.step) + 1
            return (
              <div className="app-conn" key={row.step}>
                <span className="app-conn__logo" aria-hidden="true">
                  {position}
                </span>
                <span className="app-conn__body">
                  <strong>{meta.title}</strong>
                  <span>{meta.consequence}</span>
                </span>
                <Link
                  className={`ar-btn ar-btn--sm ${row.optional ? 'ar-btn--ghost' : 'ar-btn--primary'}`}
                  to={pathFor(row.step)}
                >
                  {row.status === 'failed' ? 'Try again' : 'Set up'}
                </Link>
              </div>
            )
          })}
        </div>

        <div className="ar-card__footer">
          <Link className="ar-btn ar-btn--ghost" to={DEFAULT_HOME}>
            Back to the dashboard
          </Link>
          <span className="ar-card__spacer" />
          {state.dismissed_at ? null : (
            <Button
              tone="quiet"
              busy={dismiss.pending}
              busyLabel="Hiding the reminder"
              onClick={() => {
                void onDismiss()
              }}
            >
              Don&rsquo;t remind me again
            </Button>
          )}
        </div>
      </section>
    </div>
  )
}

/**
 * What is actually degraded, named — not "setup incomplete".
 *
 * S-D2's argument is that a tenant can only judge whether to care if the banner
 * says what they are missing, so this composes the sentence from the specific
 * steps rather than counting them.
 */
function describeDegradation(outstanding: string[]): string {
  const missing: string[] = []
  if (outstanding.includes('search_console')) {
    missing.push('the dashboard shows no click data until Search Console is connected')
  }
  if (outstanding.includes('sender')) missing.push('outreach is off until you add a sender')
  if (outstanding.includes('linkedin')) missing.push('lead discovery needs LinkedIn')
  if (outstanding.includes('keywords')) missing.push('nothing is being rank-tracked yet')
  if (missing.length === 0) return 'Everything that matters is running.'
  return `Your audit and content are running. Meanwhile, ${missing.join('; ')}.`
}

/** One line of evidence per finished step, read from what it configured. */
function summaryFor(step: string, state: ReturnType<typeof useWizard>): string {
  if (step === 'site') return state.site?.domain ?? 'Site added'
  if (step === 'keywords') {
    return `${String(state.keywords.filter((row) => row.enabled).length)} tracked`
  }
  if (step === 'brand_voice') {
    const tone = state.brand_voice?.tone ?? ''
    return TONE_LABELS[tone] ?? 'The default voice'
  }
  const connection = state.connections.find((row) =>
    row.provider.includes(step.split('_')[0] ?? ''),
  )
  return connection?.account_label ?? 'Connected'
}
